import axios from '@/api/axios';
import {
  NotificationAlertZone,
  AllNotificationAlertZoneResponse,
  AllNotificationAlertUnreadResponse,
  NotificationAlertCountUnread,
  MetaData,
  AlarmVoyager,
  AllNotificationSettingsResponse,
  NotificationSettings,
  NotificationType,
  AllNotificationTypeSettingsResponse,
  NotificatioSettingsPostResponse,
  NotificationSettingsResponse,
  NotificationSettingsAlertZone,
  NotificationSettingsAlertZoneResponse,
  DashboardSummaryResponse,
  DashboardData,
} from '@/types/api';
import apiConfig from '@/config/url-api';

import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { NotificationSetttingsFormZone } from '@/types/form';

type NotificationAlertWithMeta = {
  data: NotificationAlertZone[] | AlarmVoyager[];
  meta?: MetaData;
};

export const getAllNotificationAlert = async (
  alias: string | undefined,
  page: number = 1,
  orderBy: string | undefined,
  orderType: string | undefined,
): Promise<NotificationAlertWithMeta> => {
  const response = await axios.get<AllNotificationAlertZoneResponse>(
    apiConfig.notification.all.format({
      alias: alias,
      page: page,
      orderBy: orderBy,
      orderType: orderType,
    }),
  );
  return {
    data: response.data.data,
    meta: response.data.meta,
  };
};

export const useQueryInfiniteAllNotificationAlert = (
  alias: string,
  orderBy: string,
  orderType: string,
) => {
  const notificationAlert = useInfiniteQuery({
    queryKey: [`AllNotificationAlert`, alias],
    queryFn: ({ pageParam }) => {
      return getAllNotificationAlert(
        alias,
        pageParam ? parseInt(`${pageParam}`) : 1,
        orderBy,
        orderType,
      );
    },
    //@ts-ignore
    getNextPageParam: (lastPage: NotificationAlertWithMeta) => {
      if (lastPage?.meta && lastPage.meta?.page_number < lastPage?.meta.total_page) {
        return lastPage.meta.page_number + 1;
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    // @ts-ignore
    keepPreviousData: true,
  });

  return notificationAlert;
};

export const useQueryAllNotificationAlert = (
  alias: string | undefined,
  orderBy: string | undefined,
  orderType: string | undefined,
  page: number = 1, // accept page as argument
) => {
  const notificationAlert = useQuery({
    queryKey: ['AllNotificationAlert', alias, page, orderBy, orderType],
    queryFn: () => getAllNotificationAlert(alias, page, orderBy, orderType),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    // @ts-ignore
    keepPreviousData: true,
  });

  return notificationAlert;
};

export const getAllNotificationAlertUnReadCount =
  async (): Promise<NotificationAlertCountUnread> => {
    const response = await axios.get<AllNotificationAlertUnreadResponse>(
      apiConfig.notification.unread,
    );
    return response.data.data;
  };

export const useQueryNotificationAlertCountUnread = (): UseQueryResult<
  NotificationAlertCountUnread,
  Error
> => {
  const notificationoAlertCountUnread = useQuery({
    queryKey: [`NotificationAlertUnReadCount`],
    queryFn: getAllNotificationAlertUnReadCount,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    // @ts-ignore
    keepPreviousData: true,
  });

  // @ts-ignore
  return notificationoAlertCountUnread;
};

interface NotificationResponse {
  code: number;
  message: string;
  status: boolean;
}

export const readNotifications = async (alias: string): Promise<NotificationResponse> => {
  const response = await axios.post<NotificationResponse>(
    apiConfig.notification.read.format({ alias: alias }),
  );
  return response.data;
};

export const useMutationReadNotifications = () => {
  const queryClient = useQueryClient();

  const notificationMutation = useMutation({
    mutationFn: (alias) => readNotifications(alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['NotificationAlertUnReadCount'] });
    },
  } as UseMutationOptions<NotificationResponse, unknown, string, unknown>);

  return notificationMutation;
};

export const getAllNotificationSettings = async (): Promise<NotificationSettings[]> => {
  const response = await axios.get<AllNotificationSettingsResponse>(
    apiConfig.notification.settings,
  );

  return response.data.data;
};

export const useQueryAllNotificationSettings = () => {
  const notificationSettings = useQuery({
    queryKey: [`AllNotificationSettings`],
    queryFn: ({}) => {
      return getAllNotificationSettings();
    },
    staleTime: 1000 * 60 * 5,
  });

  return notificationSettings;
};

export const getAllNotificationTypeSettings = async (): Promise<NotificationType[]> => {
  const response = await axios.get<AllNotificationTypeSettingsResponse>(
    apiConfig.notification.type,
  );

  return response.data.data;
};

export const useQueryAllNotificationTypeSettings = () => {
  const notificationTypeSettings = useQuery({
    queryKey: [`AllNotificationTypeSettings`],
    queryFn: ({}) => {
      return getAllNotificationTypeSettings();
    },
    staleTime: 1000 * 60 * 60,
  });

  return notificationTypeSettings;
};

export const createNotificationSettings = async (
  alias: string,
  params: NotificationSetttingsFormZone,
): Promise<NotificationSettings> => {
  const response = await axios.post<NotificatioSettingsPostResponse>(
    apiConfig.notification.create.format({ alias: alias }),
    params,
  );
  return response.data.data;
};

export const useMutationCreateNotificationSettings = () => {
  const queryClient = useQueryClient();

  const createNotificationMutation = useMutation({
    mutationFn: (data: { alias: string; params: NotificationSetttingsFormZone }) =>
      createNotificationSettings(data.alias, data.params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AllNotificationSettings'] });
    },
  } as UseMutationOptions<NotificationSettings, unknown, { alias: string; params: NotificationSetttingsFormZone }, unknown>);

  return createNotificationMutation;
};

export const updateNotificationSettings = async (
  id: string,
  alias: string,
  params: NotificationSetttingsFormZone,
): Promise<NotificationSettings> => {
  const response = await axios.put<NotificatioSettingsPostResponse>(
    apiConfig.notification.single.format({ id: id, alias: alias }),
    params,
  );
  return response.data.data;
};

export const useMutationUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; alias: string; params: NotificationSetttingsFormZone }) =>
      updateNotificationSettings(data.id, data.alias, data.params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AllNotificationSettings'] });
    },
  } as UseMutationOptions<NotificationSettings, unknown, { id: string; alias: string; params: NotificationSetttingsFormZone }, unknown>);

  return updateMutation;
};

export const deleteNotificationSettings = async (id: string, alias: string): Promise<void> => {
  await axios.delete(apiConfig.notification.single.format({ id: id, alias: alias }));
};

export const useMutationDeleteNotificationSettings = () => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (data: { id: string; alias: string }) =>
      deleteNotificationSettings(data.id, data.alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AllNotificationSettings'] });
    },
  } as UseMutationOptions<void, unknown, { id: string; alias: string }, unknown>);

  return deleteMutation;
};

export const getNotificationSettingsById = async (
  alias: string,
  id: string,
): Promise<NotificationSettingsAlertZone> => {
  const response = await axios.get<NotificationSettingsAlertZoneResponse>(
    apiConfig.notification.single.format({ alias: alias, id: id }),
  );
  return response.data.data;
};

export const useQueryNotificationSettingsById = (alias: string, id: string) => {
  const notificationSettings = useQuery({
    queryKey: [`NotificationSettings`, alias, id],
    queryFn: ({}) => {
      return getNotificationSettingsById(alias, id);
    },
    staleTime: 1000 * 60 * 5,
    enabled: false,
  });

  return notificationSettings;
};

export const getDashboardSummary = async (): Promise<DashboardData> => {
  const response = await axios.get<DashboardSummaryResponse>(apiConfig.customer.dashboard);
  return response.data.data;
};

export const useDashboardSummary = () => {
  return useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => getDashboardSummary(),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};
